import { useCallback, useEffect, useState } from "react";

import CustomProjectModalUI from "./CustomProjectModal.ui.tsx";
import { CustomFormData } from "./CustomProjectModal.types.tsx";
import {
  getCustomEfficiency,
  getCustomHeatExchanger,
  getCustomLocations,
  getCustomRefrigerant,
  getCustomSystem,
  getCustomTechnology,
  getCustomVoltages,
} from "../../services/customForm.services.tsx";
import { UnitSystemOptionsType } from "../../components/Settings/Settings.types.tsx";

const getEmptyForm = (): CustomFormData => ({
  name: "",
  description: "",
  nominal_capacity: "",
  leaving_water_temperature: "",
  entering_water_temperature: "",
  ambiance_temperature: "",
  amsl: "",
  location: "",
  system_id: "",
  technology_id: "",
  refrigerant_id: "",
  heat_exchanger_id: "",
  voltage_id: "",
  efficiency_id: "",
  pressure_drop: "13.8",
  custom_system: {
    superheat: "",
    subcooling: "",
    kw_per_compressor: "",
    heat_capacity_per_compressor: "",
    compressor_rla: "",
    compressor_lra: "",
    amps_per_compressor: "",
    cooling_capacity_per_compressor: "",
    condenser_quantity_per_circuit: "0",
    compressor_quantity_per_circuit: "1",
    evaporator_quantity: "0",
    circuit_count: "1",
    condensing_temperature: "",
    evaporating_temperature: "",
    fan_quantity_per_circuit: "0",
    compressor_model: "",
    condenser_model: "",
    evaporator_model: "",
    fan_model: "",
    model_size: "",
    range_selection: "",
    is_economizer: false,
    approach_temperature: 0,
    economizer_superheat: 0,
    running_capacity: "",
    fluid_type: "",
    fluid_concentration: "",
  },
});

interface UseCustomProjectModalProps {
  onSubmit: (data: CustomFormData) => Promise<void>;
  title: string;
  buttonLabel: string;
  initialData?: CustomFormData;
  unitSystem: UnitSystemOptionsType;
}

export const useCustomProjectModal = ({
  onSubmit,
  title,
  buttonLabel,
  initialData,
  unitSystem,
}: UseCustomProjectModalProps) => {
  const [formData, setFormData] = useState<CustomFormData>(initialData ?? getEmptyForm());

  const [validated, setValidated] = useState(false);
  const [show, setShow] = useState(false);

  const [systems, setSystems] = useState(null);
  const [voltages, setVoltages] = useState(null);
  const [technology, setTechnology] = useState(null);
  const [refrigerant, setRefrigerant] = useState(null);
  const [heatExchanger, setHeatExchanger] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [locations, setLocations] = useState(null);

  useEffect(() => {
    if (initialData) setFormData(initialData);
  }, [initialData]);

  const setNestedValue = (obj: CustomFormData, path: string, value: any): CustomFormData => {
    const [parent, key] = path.replace("]", "").split("[");
    return {
      ...obj,
      [parent]: {
        ...(obj as any)[parent],
        [key]: value,
      },
    };
  };

  const handleChange = useCallback((e: any) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData((prev) =>
      name.startsWith("custom_system[")
        ? setNestedValue(prev, name, finalValue)
        : { ...prev, [name]: finalValue }
    );
  }, []);

  const handleSelectGeneric = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        if (name.startsWith("custom_system[")) {
          return setNestedValue(prev, name, value);
        }

        const updatedData = { ...prev, [name]: value };

        if (name === "location") {
          const selectedLoc = locations?.find((loc) => loc.name === value);

          if (selectedLoc) {
            return {
              ...updatedData,
              amsl: selectedLoc?.amsl?.toString(),
              ambiance_temperature: selectedLoc?.average_temperature?.toString(),
            };
          }
        }

        switch (name) {
          case "system_id":
            return {
              ...prev,
              system_id: value,
              voltage_id: "",
              technology_id: "",
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };
          case "voltage_id":
            return {
              ...prev,
              voltage_id: value,
              technology_id: "",
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };
          case "technology_id":
            return {
              ...prev,
              technology_id: value,
              refrigerant_id: "",
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };
          case "refrigerant_id":
            return {
              ...prev,
              refrigerant_id: value,
              nominal_capacity: "",
              heat_exchanger_id: "",
              efficiency_id: "",
            };
          case "nominal_capacity":
            return {
              ...prev,
              nominal_capacity: value,
              heat_exchanger_id: "",
              efficiency_id: "",
            };
          case "heat_exchanger_id":
            return {
              ...prev,
              heat_exchanger_id: value,
              efficiency_id: "",
            };
          default:
            return { ...prev, [name]: value };
        }
      });
    },
    [locations]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setValidated(true);

      if (!e.currentTarget.checkValidity()) return;

      // Si el calculo no salio, el formulario se queda como estaba: un centrifugo lo rechaza
      // Copeland y se acierta a mano, moviendo la capacidad en operacion hasta dar con una valida.
      try {
        await onSubmit(formData);
      } catch {
        return;
      }

      setFormData(getEmptyForm());
      setShow(false);
    },
    [formData, onSubmit]
  );

  useEffect(() => {
    if (show) {
      getCustomSystem().then(setSystems);
      getCustomLocations().then(setLocations);
    }
  }, [show]);

  useEffect(() => {
    if (formData.system_id) getCustomVoltages().then(setVoltages);
  }, [formData.system_id]);

  useEffect(() => {
    if (formData.voltage_id) getCustomTechnology(formData.voltage_id).then(setTechnology);
  }, [formData.voltage_id]);

  useEffect(() => {
    if (formData.technology_id) getCustomRefrigerant(formData.technology_id).then(setRefrigerant);
  }, [formData.technology_id]);

  useEffect(() => {
    if (formData.nominal_capacity && formData.system_id && formData.refrigerant_id) {
      getCustomHeatExchanger(
        formData.system_id,
        formData.refrigerant_id,
        formData.nominal_capacity
      ).then(setHeatExchanger);
    }
  }, [formData.nominal_capacity, formData.system_id, formData.refrigerant_id]);

  useEffect(() => {
    if (formData.heat_exchanger_id && formData.system_id && formData.nominal_capacity) {
      getCustomEfficiency(
        formData.nominal_capacity,
        formData.system_id,
        formData.heat_exchanger_id
      ).then(setEfficiency);
    }
  }, [formData.heat_exchanger_id, formData.system_id, formData.nominal_capacity]);

  const open = () => {
    setValidated(false);
    setShow(true);
  };

  const close = () => setShow(false);

  const modal = show ? (
    <CustomProjectModalUI
      show={show}
      title={title}
      buttonLabel={buttonLabel}
      systems={systems}
      voltages={voltages}
      technology={technology}
      refrigerant={refrigerant}
      heatExchanger={heatExchanger}
      efficiency={efficiency}
      locations={locations}
      formData={formData}
      validated={validated}
      handleChange={handleChange}
      handleSelectGeneric={handleSelectGeneric}
      handleSubmit={handleSubmit}
      handleClose={close}
      unitSystem={unitSystem}
    />
  ) : null;

  return { open, close, modal };
};
